import type { Editor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import {
  SuggestionPopoverContent,
  type SuggestionPopoverVariant,
} from "../../SuggestionPopover";
import { Body } from "../../Typography";
import type { SuggestionHighlightAttrs } from "../types";
import "./SuggestionBubbleMenu.css";

const SUGGESTION_BUBBLE_MENU_KEY = "suggestion-bubble-menu";

interface SuggestionBubbleMenuProps {
  editor: Editor | null;
  /**
   * Layout variant for the suggestion popover content.
   * - "default": Vertical layout with full details (reason, explanation, quote, confidence)
   * - "minimal": Horizontal compact layout with just reason and actions
   * Defaults to "default".
   */
  variant?: SuggestionPopoverVariant;
  onAccept: (suggestionId: string) => void;
  onReject: (suggestionId: string) => void;
  onNavigateToSegment?: (segmentId: string) => void;
}

/**
 * BubbleMenu that appears when cursor is inside a suggestion highlight.
 *
 * Shows suggestion details (reason, explanation, confidence) and Accept/Reject buttons.
 * Accept deletes the highlighted text range; Reject dismisses the suggestion.
 *
 * Design decisions:
 * - Uses TipTap BubbleMenu with shouldShow configured for suggestion marks
 * - Shows when cursor is inside a suggestionHighlight mark (user clicks highlighted text)
 * - Uses getMarkRange() to get mark positions for delete (no offset mapping needed)
 * - Accept button is disabled for invalid suggestions (content changed)
 */
export const SuggestionBubbleMenu: React.FC<SuggestionBubbleMenuProps> = ({
  editor,
  variant = "default",
  onAccept,
  onReject,
  onNavigateToSegment,
}) => {
  // Force re-render when editor selection changes
  // BubbleMenu shows/hides internally but doesn't trigger React re-renders
  const [activeSuggestion, setActiveSuggestion] =
    useState<SuggestionHighlightAttrs | null>(null);
  const bubbleMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (bubbleMenuRef.current) {
      bubbleMenuRef.current.style.zIndex = "var(--indigo-z-ant-popover)";
    }
  }, []);

  // The plugin first measures the menu before React renders its suggestion.
  // Reposition with the committed content so flip uses the actual menu size.
  useEffect(() => {
    if (!editor || !activeSuggestion) return;
    editor.view.dispatch(
      editor.state.tr.setMeta(SUGGESTION_BUBBLE_MENU_KEY, "updatePosition"),
    );
  }, [editor, activeSuggestion]);

  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      // Check if cursor is inside a suggestion
      const isActive = editor.isActive("suggestionHighlight");
      if (isActive) {
        // Pull the specific attributes for the mark at the current cursor
        setActiveSuggestion(
          editor.getAttributes(
            "suggestionHighlight",
          ) as SuggestionHighlightAttrs,
        );
      } else {
        setActiveSuggestion(null);
      }
    };

    // Listen for any cursor movement or click
    editor.on("selectionUpdate", handleUpdate);
    handleUpdate();

    return () => {
      editor.off("selectionUpdate", handleUpdate);
    };
  }, [editor]);

  // Tab deliberately enters review actions; letters in the editor remain text.
  useEffect(() => {
    if (!editor) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key !== "Tab" ||
        event.shiftKey ||
        event.ctrlKey ||
        event.altKey ||
        event.metaKey ||
        event.isComposing ||
        !editor.state.selection.empty ||
        !editor.isActive("suggestionHighlight")
      )
        return;
      // Escape may have dismissed the content without moving the cursor.
      // Render the current mark before transferring focus back into its menu.
      flushSync(() => {
        setActiveSuggestion(
          editor.getAttributes(
            "suggestionHighlight",
          ) as SuggestionHighlightAttrs,
        );
      });
      const button = bubbleMenuRef.current?.querySelector<HTMLButtonElement>(
        "[data-suggestion-keep]",
      );
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      editor.view.dispatch(
        editor.state.tr.setMeta(SUGGESTION_BUBBLE_MENU_KEY, "show"),
      );
      button.focus();
    };
    editor.view.dom.addEventListener("keydown", handleKeyDown, true);
    return () =>
      editor.view.dom.removeEventListener("keydown", handleKeyDown, true);
  }, [editor]);

  // Close bubble menu when clicking outside or using keyboard navigation
  useEffect(() => {
    if (!editor || !activeSuggestion) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      // Don't close if clicking inside the bubble menu or editor
      if (
        bubbleMenuRef.current?.contains(target) ||
        editor.view.dom.contains(target)
      ) {
        return;
      }

      setActiveSuggestion(null);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      // OPT+Arrow: timeline item navigation
      // Escape: dismiss the menu
      if (
        (event.altKey &&
          (event.key === "ArrowDown" || event.key === "ArrowUp")) ||
        event.key === "Escape"
      ) {
        setActiveSuggestion(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editor, activeSuggestion]);

  const handleAccept = useCallback(() => {
    if (!activeSuggestion || !activeSuggestion.isValid) return;

    // Use the mark's acceptSuggestion command to delete the highlighted text
    if (editor?.commands.acceptSuggestion(activeSuggestion.suggestionId)) {
      onAccept(activeSuggestion.suggestionId);
      editor.commands.focus();
      setActiveSuggestion(null);
    }
  }, [editor, activeSuggestion, onAccept]);

  const handleReject = useCallback(() => {
    if (!activeSuggestion) return;

    // Use the mark's rejectSuggestion command to remove the mark without deleting text
    // Keeping content can dismiss the stored suggestion even if its mark is gone.
    editor?.commands.rejectSuggestion(activeSuggestion.suggestionId);
    onReject(activeSuggestion.suggestionId);
    editor?.commands.focus();
    setActiveSuggestion(null);
  }, [editor, activeSuggestion, onReject]);

  // Always render BubbleMenu when editor exists - TipTap needs it mounted
  // to manage visibility via shouldShow. The content is conditionally rendered inside.
  if (!editor) return null;

  // Append to document.body for default variant to avoid clipping issues
  const appendTo = variant === "default" ? document.body : undefined;

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor }) => editor.isActive("suggestionHighlight")}
      options={{
        placement: "top",
        offset: 8,
        flip: { crossAxis: false, fallbackPlacements: ["bottom"] },
      }}
      className="indigo-suggestion-bubble-menu"
      pluginKey={SUGGESTION_BUBBLE_MENU_KEY}
      ref={bubbleMenuRef}
      appendTo={appendTo}
    >
      <div
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            editor.commands.focus();
            setActiveSuggestion(null);
          }
        }}
      >
        {activeSuggestion && (
          <>
            <Body
              size="sm"
              className="indigo-suggestion-bubble-menu__keyboard-hint"
            >
              <kbd className="indigo-suggestion-shortcut">Tab</kbd> to use K / D
              · Esc to return
            </Body>
            <SuggestionPopoverContent
              variant={variant}
              badgeLabel="Suggested Removal"
              reason={activeSuggestion.reason}
              reasonDetails={activeSuggestion.reasonDetails}
              confidence={activeSuggestion.confidence}
              isValid={activeSuggestion.isValid}
              onAccept={handleAccept}
              onReject={handleReject}
              onViewRelatedSegment={(() => {
                const relatedId = activeSuggestion.relatedSegmentID;
                return relatedId && onNavigateToSegment
                  ? () => onNavigateToSegment(relatedId)
                  : undefined;
              })()}
            />
          </>
        )}
      </div>
    </BubbleMenu>
  );
};
